import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    WifiOff, RefreshCw, CheckCircle2, AlertTriangle, ShieldAlert, Eye, EyeOff, User,
} from "lucide-react";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { displayCurrency } from "../utils/pricing";

const STATUS_STYLE = {
    OK: { bg: "bg-emerald-50", fg: "text-emerald-700", icon: CheckCircle2 },
    NO_KEY: { bg: "bg-neutral-100", fg: "text-neutral-700", icon: WifiOff },
    ERROR: { bg: "bg-red-50", fg: "text-red-700", icon: AlertTriangle },
};

export function IntegrationsTabs() {
    const loc = useLocation();
    const active = loc.pathname;
    const tabs = [
        { to: "/admin/integrations", label: "Cinematic Engine", tid: "tab-runway" },
        { to: "/admin/integrations/heygen", label: "AI Trainer", tid: "tab-heygen" },
    ];
    return (
        <div className="flex items-center gap-1 border-b border-neutral-200">
            {tabs.map((t) => (
                <Link
                    key={t.to}
                    to={t.to}
                    data-testid={t.tid}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                        active === t.to
                            ? "border-neutral-900 text-neutral-900 font-medium"
                            : "border-transparent text-neutral-500 hover:text-neutral-900"
                    }`}
                >
                    {t.label}
                </Link>
            ))}
        </div>
    );
}

export default function IntegrationsHeyGen() {
    const [view, setView] = useState(null);
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [showKey, setShowKey] = useState(false);
    const [ratio, setRatio] = useState("16:9");
    const [avatarFilter, setAvatarFilter] = useState("");
    // Default Trainer card state
    const [trainer, setTrainer] = useState(null);
    const [trainerName, setTrainerName] = useState("");
    const [voices, setVoices] = useState([]);
    const [voiceId, setVoiceId] = useState("");
    const [speechSpeed, setSpeechSpeed] = useState(1.0);
    const [savingTrainer, setSavingTrainer] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/integrations/heygen");
            setView(data);
            setRatio(data.default_ratio || "16:9");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    const loadTrainer = async () => {
        try {
            const { data } = await api.get("/trainers");
            const def = (data || []).find((t) => t.is_default) || (data || [])[0];
            if (def) {
                setTrainer(def);
                setTrainerName(def.name || "");
                setVoiceId(def.voice_id || "");
                setSpeechSpeed(def.speech_speed ?? 1.0);
            }
        } catch (e) { /* ignore — trainer optional */ }
    };
    const loadVoices = async () => {
        try {
            const { data } = await api.get("/voice/voices");
            setVoices(data || []);
        } catch (e) { /* silent */ }
    };

    const saveTrainer = async () => {
        if (!trainerName.trim()) { toast.error("Trainer name is required."); return; }
        setSavingTrainer(true);
        try {
            const payload = {
                name: trainerName.trim(),
                voice_id: voiceId || null,
                speech_speed: Number(speechSpeed),
                heygen_avatar_id: view?.default_avatar_id || trainer?.heygen_avatar_id || null,
                heygen_avatar_snapshot: view?.default_avatar_snapshot || trainer?.heygen_avatar_snapshot || null,
                provider: "heygen",
            };
            if (trainer?.id) {
                const { data } = await api.patch(`/trainers/${trainer.id}`, payload);
                setTrainer(data);
            } else {
                const { data } = await api.post("/trainers", { ...payload, is_default: true });
                setTrainer(data);
            }
            toast.success("Default trainer saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSavingTrainer(false); }
    };

    const loadAvatars = async () => {
        try {
            const { data } = await api.get("/integrations/heygen/avatars", { params: { limit: 60 } });
            setAvatars(data);
        } catch (e) {
            if (e?.response?.status === 503) return;
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); loadTrainer(); loadVoices(); }, []);
    useEffect(() => { if (view?.connection_status === "OK") loadAvatars(); }, [view?.connection_status]);

    const saveKey = async () => {
        if (!apiKey.trim()) { toast.error("Paste an API key first."); return; }
        setSaving(true);
        try {
            await api.put("/integrations/heygen", { api_key: apiKey.trim() });
            toast.success("Key saved");
            setApiKey("");
            const { data } = await api.post("/integrations/heygen/test");
            setView(data);
            if (data.connection_status === "OK") toast.success("AI Trainer connection OK");
            else if (data.connection_error) toast.error(data.connection_error);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const saveDefaults = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/integrations/heygen", { default_ratio: ratio });
            setView(data);
            toast.success("Defaults saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/integrations/heygen/test");
            setView(data);
            if (data.connection_status === "OK") toast.success("AI Trainer connection OK");
            else if (data.connection_status === "NO_KEY") toast.error("No API key saved yet");
            else toast.error(`AI Trainer error: ${data.connection_error}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(false); }
    };

    const pickAvatar = async (av) => {
        setSaving(true);
        try {
            await api.put("/integrations/heygen", {
                default_avatar_id: av.id,
                default_avatar_snapshot: av.thumbnail_url,
            });
            toast.success(`Default avatar → ${av.name}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (loading || !view) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

    const cfg = STATUS_STYLE[view.connection_status] || STATUS_STYLE.NO_KEY;
    const StatusIcon = cfg.icon;
    const filteredAvatars = avatars.filter((a) =>
        !avatarFilter || (a.name || "").toLowerCase().includes(avatarFilter.toLowerCase()) ||
        (a.gender || "").toLowerCase() === avatarFilter.toLowerCase()
    );

    return (
        <div data-testid="heygen-integration-page" className="space-y-6">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Admin · Integrations</div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">AI Trainer</h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    Talking-head trainer avatar generation using our own narrated audio. All calls happen server-side; the API key never reaches the browser.
                </p>
            </div>

            <IntegrationsTabs />

            {/* Default Trainer card — Cycle 3.5 (Trainer Studio removed) */}
            <div
                data-testid="default-trainer-card"
                className="rounded-lg border border-neutral-200 bg-white p-5 space-y-4"
            >
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-neutral-100 flex items-center justify-center">
                        <User size={16} className="text-neutral-600" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-neutral-900">Default Trainer</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                            The one on-screen presenter used across every module. Configure once — Trainer Studio replaced this in Cycle 3.5.
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs">Trainer name</Label>
                        <Input
                            data-testid="default-trainer-name"
                            value={trainerName}
                            onChange={(e) => setTrainerName(e.target.value)}
                            placeholder="e.g. Judge Trainer Two"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Voice</Label>
                        <select
                            data-testid="default-trainer-voice"
                            value={voiceId || ""}
                            onChange={(e) => setVoiceId(e.target.value)}
                            className="mt-1 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white"
                        >
                            <option value="">— pick a voice —</option>
                            {voices.map((v) => (
                                <option key={v.id} value={v.id}>{v.display_name || v.name || v.id}</option>
                            ))}
                        </select>
                        {(() => {
                            // Voice gender mismatch guard (Cycle 3.5 rebrand).
                            // OpenAI TTS voices whose perceived gender is female.
                            const FEMALE_VOICES = new Set(["nova", "shimmer", "bella"]);
                            // Known male HeyGen avatar IDs — extended over time.
                            const MALE_AVATARS = new Set([
                                "ee25a2363ef941a1b5b0f7b1172e57d1", // Dante Office 2
                            ]);
                            const avatarId = view?.default_avatar_id || trainer?.heygen_avatar_id;
                            const avatarIsMale = avatarId && MALE_AVATARS.has(avatarId);
                            const voiceIsFemale = voiceId && FEMALE_VOICES.has(voiceId.toLowerCase());
                            if (!avatarIsMale || !voiceIsFemale) return null;
                            return (
                                <div
                                    data-testid="voice-gender-mismatch-warning"
                                    className="mt-2 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
                                >
                                    <div className="text-[11px] leading-snug text-amber-900">
                                        Voice gender doesn't match avatar gender — this may feel unnatural.
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setVoiceId("onyx")}
                                        data-testid="voice-gender-quickfix"
                                        className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
                                    >
                                        Use recommended voice
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                    <div className="md:col-span-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs">Speech speed</Label>
                            <span className="text-xs mono text-neutral-600" data-testid="default-trainer-speed-value">
                                {Number(speechSpeed).toFixed(2)}×
                            </span>
                        </div>
                        <input
                            type="range" min="0.5" max="1.5" step="0.05"
                            value={speechSpeed}
                            onChange={(e) => setSpeechSpeed(parseFloat(e.target.value))}
                            data-testid="default-trainer-speed"
                            className="mt-2 w-full"
                        />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between">
                        <div className="text-xs text-neutral-500">
                            Avatar: {view?.default_avatar_id ? (
                                <span className="mono text-neutral-700">{view.default_avatar_id}</span>
                            ) : (
                                <span className="text-amber-600">Pick one from the Avatar library below</span>
                            )}
                        </div>
                        <Button
                            data-testid="default-trainer-save"
                            onClick={saveTrainer}
                            disabled={savingTrainer || !trainerName.trim()}
                            className="bg-neutral-900 text-white hover:bg-neutral-800"
                        >
                            {savingTrainer ? "Saving…" : "Save default trainer"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Connection card */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${cfg.bg}`}>
                            <StatusIcon size={18} className={cfg.fg} />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-neutral-900">Connection</div>
                            <div className={`text-xs ${cfg.fg}`} data-testid="heygen-connection-status">
                                {view.connection_status}
                                {view.connection_error ? ` · ${view.connection_error}` : ""}
                            </div>
                            {view.has_api_key && (
                                <div className="mono text-[10px] text-neutral-400 mt-0.5">
                                    Stored key: {view.api_key_masked}
                                </div>
                            )}
                            {view.last_checked_at && (
                                <div className="mono text-[10px] text-neutral-400">
                                    Last checked {new Date(view.last_checked_at).toLocaleString()}
                                </div>
                            )}
                        </div>
                    </div>
                    <Button variant="outline" onClick={test} disabled={testing} data-testid="heygen-test-btn">
                        <RefreshCw size={13} className={`mr-1.5 ${testing ? "animate-spin" : ""}`} />
                        Test connection
                    </Button>
                </div>

                {view.connection_status === "NO_KEY" && (
                    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                        <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                        <div>
                            No API key saved yet. Paste your key below — it's stored server-side under this organisation's integrations, encrypted at rest by MongoDB, and never returned to the browser.
                        </div>
                    </div>
                )}

                <div className="mt-5 grid grid-cols-[1fr,auto] gap-3">
                    <div>
                        <Label className="text-xs">AI Trainer API key</Label>
                        <div className="relative mt-1">
                            <Input
                                type={showKey ? "text" : "password"}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Paste your AI Trainer API key"
                                data-testid="heygen-key-input"
                                className="pr-10 font-mono text-xs"
                            />
                            <button
                                type="button"
                                onClick={() => setShowKey(!showKey)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-900"
                                data-testid="heygen-key-toggle"
                            >
                                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                        <div className="mt-1 text-[10px] text-neutral-500">
                            Obtain your API key from the AI Trainer provider console.
                            Requires prepaid balance. Never share this key.
                        </div>
                    </div>
                    <div className="self-end">
                        <Button onClick={saveKey} disabled={saving || !apiKey.trim()}
                            data-testid="heygen-save-key-btn"
                            className="bg-neutral-900 text-white hover:bg-neutral-800">
                            {saving ? "Saving…" : "Save & test"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Defaults & avatar library */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-neutral-900">Default avatar</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                            Auto-selected when a new trainer profile is created. Trainer Studio can override per-trainer.
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <select value={ratio} onChange={(e) => setRatio(e.target.value)}
                            data-testid="heygen-ratio-select"
                            className="rounded-md border border-neutral-200 px-2 py-1 text-sm">
                            <option value="16:9">16:9</option>
                            <option value="9:16">9:16</option>
                        </select>
                        <Button variant="outline" size="sm" onClick={saveDefaults}
                            disabled={saving} data-testid="heygen-save-defaults-btn">
                            Save ratio
                        </Button>
                    </div>
                </div>

                {view.default_avatar_id ? (
                    <div className="rounded-md border border-neutral-200 p-3 flex items-center gap-3">
                        {view.default_avatar_snapshot ? (
                            <img src={view.default_avatar_snapshot} alt="" className="h-14 w-14 rounded object-cover" />
                        ) : (
                            <div className="h-14 w-14 rounded bg-neutral-100" />
                        )}
                        <div className="text-sm">
                            <div className="font-medium text-neutral-900">Selected avatar</div>
                            <div className="mono text-[11px] text-neutral-500">{view.default_avatar_id}</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-neutral-500">No default avatar chosen yet.</div>
                )}

                {view.connection_status === "OK" && (
                    <>
                        <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Avatar library ({avatars.length})</div>
                            <Input value={avatarFilter} onChange={(e) => setAvatarFilter(e.target.value)}
                                placeholder="Search name or gender" className="max-w-xs text-xs"
                                data-testid="heygen-avatar-filter" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {filteredAvatars.map((av) => (
                                <button
                                    key={av.id}
                                    onClick={() => pickAvatar(av)}
                                    data-testid={`heygen-avatar-${av.id}`}
                                    className={`text-left rounded-md border overflow-hidden hover:border-neutral-400 transition-colors ${
                                        view.default_avatar_id === av.id
                                            ? "border-neutral-900 ring-2 ring-neutral-900/10"
                                            : "border-neutral-200"
                                    }`}
                                >
                                    <div className="aspect-video bg-neutral-900">
                                        {av.thumbnail_url && <img src={av.thumbnail_url} alt="" className="h-full w-full object-cover" />}
                                    </div>
                                    <div className="p-2">
                                        <div className="text-sm font-medium text-neutral-900 truncate">{av.name}</div>
                                        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-neutral-500">
                                            {av.gender && <span>{av.gender}</span>}
                                            {av.engine && <span className="mono">{av.engine}</span>}
                                            {av.is_premium && <span className="rounded bg-amber-50 px-1 text-amber-800">premium</span>}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Pricing */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5">
                <div className="text-sm font-semibold text-neutral-900 mb-3">Pricing snapshot</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    {Object.entries(view.price_per_second_usd).map(([engine, price]) => (
                        <div key={engine} className="rounded-md border border-neutral-200 p-3 flex items-center justify-between">
                            <span className="text-neutral-700">{engine.replaceAll("_", " ")}</span>
                            <span className="mono text-neutral-900">${displayCurrency(price, 4)}/s</span>
                        </div>
                    ))}
                </div>
                <p className="mt-2 text-[10px] text-neutral-500">
                    Confirm the current engine for each avatar before running bulk generation.
                    A soft warning fires at 80% of the ${displayCurrency(8)} per-render budget cap.
                </p>
            </div>
        </div>
    );
}
